import React, { Fragment, useRef, memo } from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import { Container, Typo, useLazyLoadImage } from 'tmt-ui-kit';

import HeadTag from '~/components/Head/HeadTag';
import config from '~/config';

const Breadcrumbs = dynamic(
  () => import(/* webpackChunkName: "Breadcrumbs.Breadcrumbs.components" */'../../components/Breadcrumbs/Breadcrumbs'),
  { loading: () => null }
);

const Banner = dynamic(
  () => import(/* webpackChunkName: "Banner.PageBanner.components" */'../../components/Banner/PageBanner'),
  { loading: () => null }
);

const InnerContainer = styled(Container.InnerContainer)`
  padding-top: 16px;
  padding-bottom: 60px;
`;

const Title = styled.span`
  display: block;
`;

const TitleTopic = styled(Typo.HeaderThree)`
  font-size: 24px !important;
  margin-bottom: 30px;
`;

const Text = styled(Typo.Paragraph)`
  font-size: 16px !important;
`;

const ContentContainer = styled.div`
  width: 1000px;
  margin: 0 auto;

  @media (max-width: 1000px) {
    width: 100%;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.reverse ? '1fr 40%' : '40% 1fr')};
  grid-column-gap: 30px;
  margin-bottom: 30px;

  @media (max-width: 600px) {
    div:first-child {
      grid-row: ${props => (props.reverse ? '2' : '1')};
    }
    grid-template-columns: 1fr;
  }
`;

const Col = styled.div`
  align-self: center;
`;

const Image = styled.img`
  border-radius: 8px;
`;

function renderTitle() {
  return (
    <div>
      <Title>Experiences hosted by locals</Title>
    </div>
  );
}

const GoWithLocals = () => {
  const images = [useRef(), useRef(), useRef(), useRef()];
  useLazyLoadImage(images[0]);
  useLazyLoadImage(images[1]);
  useLazyLoadImage(images[2]);
  useLazyLoadImage(images[3]);

  return (
    <Fragment>
      <HeadTag
        title="Hosted by locals - TakeMeTour"
      />
      <Banner
        renderTitle={renderTitle}
        backgroundImage={`${config.assetPath}/static/images/Hosted-by-locals/DSC03739.jpg`}
      />
      <InnerContainer>
        <Breadcrumbs
          type="hosted-by-locals"
          customStyle={{ marginBottom: 16 }}
        />
        <TitleTopic>Experiences hosted by locals</TitleTopic>
        <ContentContainer>
          <Row>
            <Col>
              <Image ref={images[0]} alt="content1" data-src={`${config.assetPath}/static/images/Hosted-by-locals/content1.jpg`} />
            </Col>
            <Col>
              <Text>On TakeMeTour, trips are hosted by real locals who know their hometowns like no other – we call them the <a href="/trust-and-safety" target="_blank">Local experts</a>. Rather than a tour guide going through the motions, they are like your <a href="/about-us" target="_blank">local friend</a> who connects and shares stories with you. With them, you’ll eat and go where the locals do, and truly get an insider’s experience of the culture.</Text>
            </Col>
          </Row>
          <Row reverse>
            <Col>
              <Text>If you’re wary of tourist attractions, our <a href="/trust-and-safety" target="_blank">Local experts</a> know exactly which to avoid and which are the real deal. They can even take you to places you didn’t know existed and give you the warmest welcome when you arrive.</Text>
            </Col>
            <Col>
              <Image ref={images[1]} alt="content2" data-src={`${config.assetPath}/static/images/Hosted-by-locals/content2.jpg`} />
            </Col>
          </Row>
          <Row>
            <Col>
              <Image ref={images[2]} alt="content3" data-src={`${config.assetPath}/static/images/Hosted-by-locals/content3.jpg`} />
            </Col>
            <Col>
              <Text>Worried about the language barrier? Don’t be. Our <a href="/trust-and-safety" target="_blank">Local experts</a> can all speak English, and we are constantly looking for more <a href="/trust-and-safety" target="_blank">Local experts</a> who can speak other languages to bring you an authentic and meaningful experience.</Text>
            </Col>
          </Row>
          <Row reverse>
            <Col>
              <Text>Our <a href="/trust-and-safety" target="_blank">Local experts</a> also go through a thorough background check to ensure your safety, and all tours come with free accident insurance. We also have a 100% Satisfaction Guarantee policy, which means you only pay when you are satisfied! <br/><br/><br/>So, are you ready to go with the locals? Get inspired today with our vast array of local experiences and book your tour hassle-free.</Text>
            </Col>
            <Col>
              <Image ref={images[3]} alt="content4" data-src={`${config.assetPath}/static/images/Hosted-by-locals/content4.jpg`} />
            </Col>
          </Row>
        </ContentContainer>
      </InnerContainer>
    </Fragment>
  );
};

export default memo(GoWithLocals, () => true);
